import React, { Component } from 'react';
import { Upload as AntUpload, message, Icon } from 'antd';
import { services } from '@comall-backend-builder/core';
import { each, find } from 'lodash';
import {
    UploadProps as AntUploadProps,
    RcFile as AntRcFile,
    UploadChangeParam as AntUploadChangeParam,
    RcCustomRequestOptions as AntRcCustomRequestOptions,
    UploadFile as AntUploadFile,
} from 'antd/lib/upload/interface';
import { CustomStyleComponentProps, ExtendedParentComponentProps } from '@comall-backend-builder/components-basis';

/**
 * 上传完成后的回调参数
 */
type uploadCompleteParams = {
    /**
     * 上传成功的文件数量
     */
    success: number;

    /**
     * 上传失败的文件数量
     */
    error: number;
};

/**
 * 超出文件大小限制回调参数
 */
type uploadLimitSizeErrorParams = {
    /**
     * 当前的上传文件
     */
    file: AntRcFile;

    /**
     * 上传文件的最大文件大小限制，单位kb
     */
    maxSize: number;
};

/**
 * 超出数量限制回调参数
 */
type uploadLimitErrorParams = {
    /**
     * 上传最大数量
     */
    maxCount: number;
};

/**
 * 图片value，从后台接口中获取到的数据
 */
export type FileValue = {
    /**
     * 文件id
     */
    id: string;

    /**
     * 文件路径地址
     */
    path: string;
};

/**
 * 实际组件暴露的 Props
 *
 * 是由转发的 antd Props 组合组件自身的 Props 组成的
 */
interface VideoFileUploadProps
    extends Pick<AntUploadProps, 'accept' | 'multiple'>,
        CustomStyleComponentProps,
        ExtendedParentComponentProps {
    /**
     * 属性名称，实体的属性key，与 antd 的 name 属性不同，从父级组件传递过来
     */
    name: string;

    /**
     * 上传文件选择完成后的回调，由props传入
     * @param value 新值
     * @param name 输入组件的 name，作为该输入组件在其所属表单内的唯一识别符
     */
    onChange?: (value: any, name: string) => {};

    /**
     * 已上传的图片
     */
    value?: FileValue[];

    /**
     * 发到后台的文件参数名，与 antd 的 name 属性相同，必传
     */
    fileName: string;

    /**
     * 图片要上传的接口地址，与 antd 的 action 属性相同，但仅使用 string 类型
     */
    uploadUrl: string;

    /**
     * 图片上传的类型，multi: 上传多张, single: 上传单张
     */
    uploadType?: 'multi' | 'single';

    /**
     * 上传组件的模式
     */
    mode?: 'default' | 'text' | 'picture-card' | 'drag';

    /**
     * 上传最大数量
     */
    maxCount?: number;

    /**
     * 上传数量限制提示文本，例如 最多上传 3 张
     */
    limitTips?: {
        /**
         * 自定义类名
         */
        className?: string;

        /**
         * 自定义属性
         */
        style?: React.CSSProperties;

        /**
         * 限制文本，例如 最多上传
         */
        limitText?: string;

        /**
         * 限制单位，例如 张
         */
        limitUnit?: string;
    };

    /**
     * 上传文件的最大文件大小限制，单位kb
     */
    maxSize?: number;

    /**
     * 是否支持排序，默认false。当 mode为 picture-card 时候排序有效
     */
    sortable?: boolean;

    /**
     * 上传结束提醒信息
     */
    uploadCompleteMessage?: string;

    /**
     * 超出文件大小限制提醒信息
     */
    uploadLimitSizeErrorMessage?: string;

    /**
     * 超出数量限制提醒信息
     */
    uploadLimitErrorMessage?: string;

    /**
     * 上传完成后回调
     */
    onUploadComplete?: (data: uploadCompleteParams) => {};

    /**
     * 超出文件大小限制回调
     */
    onUploadLimitSizeError?: (data: uploadLimitSizeErrorParams) => {};

    /**
     * 超出数量限制回调
     */
    onUploadLimitError?: (data: uploadLimitErrorParams) => {};
}

/**
 * 组件状态
 */
interface VideoFileUploadState {
    /**
     * 处理同步初始化数据
     */
    fileList: AntUploadFile[];

    /**
     * 当前预览图片的url
     */
    previewImage?: string;

    /**
     * modal 是否可见，同 modal visible 属性
     */
    previewVisible?: boolean;

    /**
     * 当前需要显示的面板索引，同 carousel activePanel 属性
     */
    activePanel?: number;
}

export class VideoFileUpload extends Component<VideoFileUploadProps, VideoFileUploadState> {
    /**
     * 当前正在上传的文件队列，为了上传成功后统计上传结果
     */
    uploadQueue: any[] = [];

    /**
     * 当前的文件列表
     */
    localFileList: AntUploadFile[];

    /**
     * 当前已上传文件数量
     */
    uploadCount: number = 0;

    constructor(props: any) {
        super(props);
        const { value } = props;
        this.state = {
            // 处理同步初始化数据
            fileList: value && value.length ? this.conventValueToFileList(value) : [],
            previewImage: '',
            previewVisible: false,
            activePanel: 0,
        };
        this.localFileList = [];
        this.handleComponentChange = this.handleComponentChange.bind(this);
        this.handleFileListChange = this.handleFileListChange.bind(this);
        this.handleCustomRequest = this.handleCustomRequest.bind(this);
        this.conventValueToFileList = this.conventValueToFileList.bind(this);
        this.handleBeforeUpload = this.handleBeforeUpload.bind(this);
    }

    /**
     * Upload组件的onChange方法
     * info 上传的文件信息
     */
    handleComponentChange(info: AntUploadChangeParam) {
        const {
            onUploadComplete,
            uploadCompleteMessage,
        } = this.props;
        let fileList: AntUploadFile[] = [];
        let infoFileList = info.fileList;
        let status = info.file.status;

        if (infoFileList.length) {
            fileList = [infoFileList.pop()!];
        }

        if (status === 'uploading') {
            this.setState({ fileList: fileList });
        } else if (status === 'removed') {
            this.handleFileListChange(fileList);
        } else if (status === 'done' || status === 'error') {
            if (status === 'done') {
                this.localFileList.push(info.file);
                if (infoFileList.every((item) => item.status === 'done')) {
                    this.handleFileListChange(fileList);
                }
            }
            if (infoFileList.every((item) => item.status === 'done' || item.status === 'error')) {
                let result = this.getComplateResult(this.uploadQueue, fileList);
                if (uploadCompleteMessage !== null) {
                    let completeMessage =
                        uploadCompleteMessage === undefined
                            ? services.language.getText(
                                  'components.UploadFile.uploadCompleteMessage'
                              )
                            : uploadCompleteMessage;

                    message.success(
                        services.interpolate(completeMessage, {
                            success: result.success + '',
                            error: result.error + '',
                        })
                    );
                }
                if (onUploadComplete) {
                    onUploadComplete(result);
                }
                this.uploadQueue = [];
            }
        }
    }

    // 获取本次的上传结果，成功数量、失败数量
    getComplateResult(uploadQueue: any[], uploadResult: AntUploadFile[]) {
        let result = { success: 0, error: 0 };
        each(uploadQueue, function(item) {
            let upload = find(uploadResult, function(i) {
                return i.uid === item.uid;
            });
            if (!upload) {
                return;
            }
            if (upload.status === 'done') result.success++;
            if (upload.status === 'error') {
                result.error++;
            }
        });
        return result;
    }

    /**
     * Upload组件的beforeUpload方法，用来检测当前上传是否超出最大限制
     * 以uploadCount计数本次上传的文件，在上传文件总数 = 最大限制数量 + 1时提示超限，当计数值达到本次上传总数时请零
     */
    handleBeforeUpload(file: AntRcFile, fileList: AntRcFile[]) {
        const {
            maxCount,
            maxSize,
            uploadLimitErrorMessage,
            uploadLimitSizeErrorMessage,
            onUploadLimitSizeError,
            onUploadLimitError,
        } = this.props;

        let uploadedLength: number = 0;
        let { fileList: stateFileList } = this.state;
        if (stateFileList && stateFileList.length) {
            uploadedLength = stateFileList.length;
        }
        if (!this.uploadCount) {
            this.uploadCount = 0;
        }
        this.uploadCount++;

        if (maxSize) {
            if (file.size > maxSize * 1024) {
                if (uploadLimitSizeErrorMessage !== null) {
                    let errorMessage =
                        uploadLimitSizeErrorMessage === undefined
                            ? services.language.getText(
                                  'components.UploadFile.uploadLimitSizeErrorMessage'
                              )
                            : uploadLimitSizeErrorMessage;
                    message.error(services.interpolate(errorMessage, { file, maxSize }));
                }
                if (onUploadLimitSizeError) {
                    onUploadLimitSizeError({ file, maxSize });
                }
                this.uploadCount--;
                return false;
            }
        }

        if (maxCount && uploadedLength + this.uploadCount > maxCount) {
            if (uploadedLength + this.uploadCount === maxCount + 1) {
                if (uploadLimitErrorMessage !== null) {
                    let errorMessage =
                        uploadLimitErrorMessage === undefined
                            ? services.language.getText(
                                  'components.UploadFile.uploadLimitErrorMessage'
                              )
                            : uploadLimitErrorMessage;
                    message.error(services.interpolate(errorMessage, { maxCount }));
                }
                if (onUploadLimitError) {
                    onUploadLimitError({ maxCount });
                }
            }
            if (this.uploadCount === fileList.length) {
                this.uploadCount = 0;
            }
            this.uploadCount--;
            return false;
        }
        return true;
    }

    /**
     * 调用表单的onChange方法
     * fileList 上传列表里的文件数组
     */
    handleFileListChange(fileList: AntUploadFile[]) {
        const { onChange, name } = this.props;
        let transformFileList: Array<object>;
        transformFileList = fileList.map((item) => {
            return {
                id: item.response.id,
                path: item.response.path,
            };
        });
        if (onChange) {
            onChange(transformFileList, name);
        }
    }

    async handleCustomRequest(fileInfo: AntRcCustomRequestOptions) {
        const { params, fileName, uploadUrl } = this.props;
        const file = fileInfo.file;
        let data = {
            files: file,
            otherParams: params,
        };
        let config: any = {
            progressCallBack: (percent: number) => {
                fileInfo.onProgress({ percent: percent }, file);
            },
            fileName,
            apiRoot: uploadUrl,
            apiPath: '',
        };
        try {
            if (!this.uploadQueue) {
                this.uploadQueue = [file];
            } else {
                this.uploadQueue.push(file);
            }
            const response = await services.api.upload(data, config);
            fileInfo.onSuccess({ id: response.id, path: response.path }, file);
        } catch (e) {
            fileInfo.onError(e);
        }
    }

    render() {
        const { className, style, fileName, accept, multiple, uploadUrl, maxCount, mode, maxSize } = this.props;

        let props = {
            name: fileName,
            action: uploadUrl,
            accept,
            multiple,
            fileList: this.state.fileList,
            className,
            style,
            maxCount,
            onChange: this.handleComponentChange,
            customRequest: this.handleCustomRequest,
            beforeUpload: this.handleBeforeUpload,
            listType: mode
        };

        return (
            <div className="upload-main">
                <div className="upload-content">{this.renderUploadComponent(props)}</div>
                {maxSize ? this.renderLimitText(maxSize) : null}
            </div>
        );
    }

    /**
     * 渲染文件上传限制提示文字
     */
    renderLimitText(maxSize: number) {
        return (
            <div className='upload-limit-text'>
                上传视频格式为.mp4，并且不超过{maxSize / 1024}M
            </div>
        );
    }

    componentWillReceiveProps(nextProps: any) {
        let fileList = this.conventValueToFileList(nextProps.value);
        this.setState({ fileList });
    }

    /**
     * 转换value为Upload组件可识别的文件数据格式列表
     * value 原数据集合
     * Upload组件可识别的文件数据格式列表
     */
    conventValueToFileList(value: FileValue[]) {
        let fileList: AntUploadFile[] = [];
        if (value && value.length) {
            fileList = value.map((valueItem) => this.getFileItemInfo(valueItem));
        }
        return fileList;
    }

    /**
     * 获取上传文件列表的每一项文件的信息
     * valueItem: 每一项文件的地址和id
     * 返回 每一项文件的信息
     */
    getFileItemInfo(valueItem: FileValue) {
        let localList =
            this.localFileList && this.localFileList.length
                ? this.localFileList.filter((item) => item.response.id === valueItem.id)
                : [];
        if (localList && localList.length) {
            return Object.assign({}, localList[0], { url: localList[0].response.path });
        } else {
            let itemName = valueItem.path.split('/').pop()!;
            let status: 'error' | 'success' | 'done' | 'uploading' | 'removed' = 'done';
            return {
                response: { id: valueItem.id, path: valueItem.path },
                url: valueItem.path,
                name: itemName,
                status,
                percent: 100,
                uid: valueItem.id,
                size: 0, // 需要知道当前图片的大小，单位为kb
                type: '', // 需要知道当前图片的类型
            };
        }
    }

    /**
     * 获取picture-card模式的上传组件
     * componentProps 所要渲染组件的props
     */
    renderUploadComponent(componentProps: any) {
        return (
            <AntUpload {...componentProps}>
                {this.state.fileList.length >= componentProps.maxCount ? null : (
                    <div>
                        <Icon type="plus" />
                        <div className="ant-upload-text">上传</div>
                    </div>
                )}
            </AntUpload>
        );
    }

}

/**
 * 实际组件暴露的 Props
 */
export interface VideoUploadProps {
    /**
     * 属性名称，实体的属性key，与 antd 的 name 属性不同，从父级组件传递过来
     */
    name: string;

    /**
     * 图片要上传的接口地址，与 antd 的 action 属性相同，但仅使用 string 类型
     */
    uploadUrl: string;

    /**
     * 已上传的图片
     */
    value?: string[] | string;

    /**
     * 上传文件选择完成后的回调，由props传入
     * @param value 新值
     * @param name 输入组件的 name，作为该输入组件在其所属表单内的唯一识别符
     */
    onChange?: (value: any, name: string) => {};

    /**
     * 发到后台的文件参数名，与 antd 的 name 属性相同，必传
     */
    fileName: string;

    /**
     * 其他属性
     */
    [key: string]: any;
}

export class VideoUpload extends Component<VideoUploadProps> {
    onChange = (value: any, name: any) => {
        let { onChange } = this.props;
        let _value: string = '';
        if (Array.isArray(value) && value.length) {
            _value = value[0].path;
        }
        if (onChange) {
            onChange(_value, name);
        }
    };

    render() {
        let { value } = this.props;
        let valueArr: FileValue[] = [];
        if (value && typeof value === 'string') {
            valueArr = [{ id: value.split('/').pop()!, path: value }];
        }
        let props = Object.assign({}, this.props, {
            value: valueArr,
            onChange: this.onChange,
        });
        return <VideoFileUpload {...props} />;
    }
}
